<template>
  <div v-on-clickaway="onClickOutside"
       class="relative w-full md:w-auto"
  >
    <!-- Search input -->
    <form
      id="recipe-search-form"
      class="relative"
      @submit.prevent="onSearchSubmit"
    >
      <input
        type="text"
        :value="searchTerm"
        :aria-label="translations.searchPlaceholderText"
        :placeholder="translations.searchPlaceholderText"
        class="w-full shadow-button text-xs bg-white py-3 ltr:pl-4 ltr:pr-12 rtl:pl-12 rtl:pr-4"
        @focus="onInputFocus"
        @blur="onInputBlur"
        @input="searchTerm = $event.target.value"
      >

      <div v-if="loading" class="flex items-center absolute inset-y-0 ltr:right-0 rtl:left-0 px-4">
        <div class="spinner" />
      </div>

      <button v-else-if="isSearchActive"
              class="flex items-center absolute inset-y-0 ltr:right-0 rtl:left-0 px-4"
              @click="onClearSearch"
      >
        <span class="icon-close" />
      </button>

      <button
        v-else
        type="submit"
        class="flex items-center absolute inset-y-0 ltr:right-0 rtl:left-0 px-4"
      >
        <span class="icon-search" />
      </button>
    </form>

    <!-- Search results -->
    <transition enter-active-class="transition-all duration-500 ease-out"
                leave-active-class="transition-all duration-500 ease-out"
                enter-class="max-h-0"
                enter-to-class="max-h-screen"
                leave-class="max-h-screen"
                leave-to-class="max-h-0"
    >
      <ul v-if="!loading && activeSearchResult && isDropdownActive"
          class="absolute w-full overflow-hidden bg-white shadow-dropdown px-6 pb-3 z-10"
      >
        <li v-if="activeSearchResult.items.length === 0" class="border-t border-beige pt-3">
          <div v-html="translations.noResultAutosuggestionText" />
        </li>

        <li v-for="(item, index) in activeSearchResult.items"
            :key="index"
            class="border-t border-beige py-3"
        >
          <a
            :href="item.url"
            class="flex items-center justify-between hover:text-blue-light group"
            @click="onItemClick(item.hitId, index + 1)"
          >
            <span v-html="item.name" />
            <span class="icon-arrow-left text-xs mr-4 transform duration-200 group-hover:-translate-x-2" />
            <span class="icon-arrow-right text-xs ml-4 transform duration-200 group-hover:translate-x-2" />
          </a>
        </li>
        <li
          v-if="activeSearchResult.totalCount > 1"
          class="border-t border-beige py-3"
        >
          <a
            href="#"
            class="text-xs flex items-center justify-center hover:text-blue-light group"
            @click.prevent="onSearchSubmit"
          >
            <span class="border-b-2 border-beige">{{ seeAllResultsText }}</span>
          </a>
        </li>
      </ul>
    </transition>
  </div>
</template>

<script>
import { mixin as clickaway } from 'vue-clickaway'
import {
  computed, watch, onMounted, ref
} from 'vue'

import quickSearchFactory from '@/compositions/search/QuickSearchFactory'
import searchQueryState from '@/compositions/search/SearchQuery'
import searchConfig from '@/compositions/search/SearchConfiguration'
import api from '@/api'

export default {
  mixins: [clickaway],
  setup() {
    const searchTerm = ref('')
    const debouncedInput = ref('')
    const isSearchActive = ref(false)
    const isInputInFocus = ref(false)
    const isDropdownActive = ref(false)
    let timeout = null

    const {
      language, activeTerms, setTerms, removeAllCategories
    } = searchQueryState.searchQueryUse()
    const { getActiveConfig } = searchConfig.searchConfigurationUse()
    const activeConfig = getActiveConfig()
    const {
      loading, setTerm, searchResults, resetQuickSearchResults
    } = quickSearchFactory.quickSearchUse(language, activeConfig.type)

    const activeSearchResult = computed(() => searchResults.value.find((x) => x.type == activeConfig.type))

    if (activeTerms.value && activeTerms.value.length > 0) {
      const [term] = activeTerms.value
      searchTerm.value = term
    }

    watch(searchTerm, (val) => {
      if (isInputInFocus.value) {
        if (timeout) clearTimeout(timeout)
        isSearchActive.value = false

        if (val.length < 3) {
          isDropdownActive.value = false
        }

        timeout = setTimeout(() => {
          debouncedInput.value = val

          if (val.trim().length >= 3) {
            isDropdownActive.value = true
            setTerm(val)
          }
        }, 500)
      }
    })

    onMounted(() => {
      if (searchTerm.value) {
        isSearchActive.value = true
      }
    })

    function onInputBlur() {
      isInputInFocus.value = false
    }

    function onInputFocus() {
      isInputInFocus.value = true
      if (debouncedInput.value.length > 2) {
        isDropdownActive.value = true
      }
    }

    function onClickOutside() {
      isDropdownActive.value = false
    }

    function performSearch() {
      if (timeout) clearTimeout(timeout)
      setTerms([searchTerm.value])
      if (searchTerm.value) {
        isSearchActive.value = true
      }
      removeAllCategories()
      resetQuickSearchResults()
    }

    function onSearchSubmit() {
      if (searchTerm.value.trim().length < 1) {
        return
      }
      performSearch()
    }

    function onClearSearch() {
      searchTerm.value = ''
      isSearchActive.value = false
      performSearch()
    }

    function onItemClick(hitId, position) {
      api.trackAutoSuggestionClick({
        searchType: activeConfig.type,
        trackId: activeSearchResult.trackId,
        hitId,
        query: encodeURIComponent(searchTerm.value),
        position,
        totalCount: activeSearchResult.totalCount
      })
    }

    return {
      loading,
      isSearchActive,
      isInputInFocus,
      isDropdownActive,
      searchTerm,
      activeSearchResult,
      translations: computed(() => getActiveConfig().translations),
      seeAllResultsText: computed(() => getActiveConfig().translations.seeAllResultsAutosuggestionText.replace('{0}', activeSearchResult.value.totalCount)),

      onInputFocus,
      onInputBlur,
      onItemClick,
      onClickOutside,
      onSearchSubmit,
      onClearSearch
    }
  }
}
</script>
